import classNames from "classnames";
import has from "lodash/has";
import isArray from "lodash/isArray";
import map from "lodash/map";
import { Pill } from "PFComponents/pill";
import { Typography } from "PFComponents/typography";
import { Variant } from "PFComponents/typography/typography.types";
import { GenericActivityEvent } from "PFTypes";
import { useTranslation } from "react-i18next";

import { useSanitizedAttributes } from "../../sanitize_history_entry";
import css from "./history_updates_attributes.module.scss";
import { UpdatedAttribute } from "./updated_attribute";

const LabelLevelVariants: Variant[] = ["h5", "bodyBold", "bodyRegular"];

type HistoryUpdatedAttributesProps = {
  level?: number;
  label?: string;
  updatedAttributes: GenericActivityEvent["diff"];
  metadata: GenericActivityEvent["metadata"];
};

export const HistoryUpdatedAttributes = ({
  label,
  level = 0,
  updatedAttributes,
  metadata
}: HistoryUpdatedAttributesProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.history.entry.updatedAttributes" });

  const isValueRemoved = has(updatedAttributes, "id") && updatedAttributes.id![1] === null;
  const isValueAdded = has(updatedAttributes, "id") && updatedAttributes.id![0] === null;
  const isLowestLevel = isArray(updatedAttributes);
  const sanitizedAttributes = useSanitizedAttributes(updatedAttributes, metadata, label);

  const renderLabel = (label: string) =>
    t(`labels.${label}` as unknown as TemplateStringsArray, { defaultValue: label });

  const renderUpdatedAttributes = (attributes: GenericActivityEvent["diff"]) =>
    map(attributes, (attribute: GenericActivityEvent["diff"], key: string) => (
      <HistoryUpdatedAttributes
        key={key}
        label={key}
        updatedAttributes={attribute}
        level={level + 1}
        metadata={metadata}
      />
    ));

  return (
    <div className={classNames(css.container, { [css.indent]: level > 1 })}>
      {label && (
        <Typography
          className={css.updateAttributeTitle}
          variant={LabelLevelVariants[level - 1] || "labelBold"}
        >
          {renderLabel(label)}
        </Typography>
      )}
      {isValueAdded && (
        <Pill kind="success" small>
          {t("added")}
        </Pill>
      )}
      {isValueRemoved && (
        <Pill kind="danger" small>
          {t("removed")}
        </Pill>
      )}
      {!isLowestLevel ? (
        !isValueRemoved && renderUpdatedAttributes(sanitizedAttributes)
      ) : (
        <UpdatedAttribute from={sanitizedAttributes[0]} to={sanitizedAttributes[1]} />
      )}
    </div>
  );
};
